import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import TinymceEditor from 'components/common/TinymceEditor';
import { createBlogPost, fetchBlogPost, updateBlogPost } from 'helpers/blog';

const EMPTY_FORM = {
  title: '',
  subtitle: '',
  category: '',
  author_name: '',
  excerpt: '',
  content: '',
  cover_image_url: '',
  accent_color: '#0b5ed7',
  reading_time: '',
  meta_title: '',
  meta_description: '',
  is_featured: false,
  is_published: false
};

const BlogEditorPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { postId } = useParams();
  const isEditing = Boolean(postId);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const loadPost = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchBlogPost(postId);
        setForm({
          ...EMPTY_FORM,
          ...response.post
        });
      } catch (requestError) {
        setError(requestError.response?.data?.error || t('BLOG.ERROR_LOAD'));
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [isEditing, postId, t]);

  const handleChange = event => {
    const { name, value, type, checked } = event.target;
    setForm(current => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      const response = isEditing
        ? await updateBlogPost(postId, payload)
        : await createBlogPost(payload);
      toast.success(t(isEditing ? 'BLOG.TOAST_UPDATED' : 'BLOG.TOAST_CREATED'), { theme: 'colored' });
      navigate(`/blog/${response.post.id}`);
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('BLOG.ERROR_SAVE'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="d-flex justify-content-center py-6"><Spinner animation="border" /></div>;
  }

  return (
    <>
      <PageHeader
        title={t(isEditing ? 'BLOG.EDIT_TITLE' : 'BLOG.CREATE_TITLE')}
        description={t('BLOG.EDITOR_DESCRIPTION')}
        className="mb-4"
      />

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Form onSubmit={handleSubmit}>
        <Row className="g-4">
          <Col lg={8}>
            <Card className="border">
              <Card.Header>
                <h5 className="mb-0">{t('BLOG.CONTENT_SECTION')}</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.TITLE')}</Form.Label>
                  <Form.Control name="title" value={form.title} onChange={handleChange} required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.SUBTITLE')}</Form.Label>
                  <Form.Control name="subtitle" value={form.subtitle} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.EXCERPT')}</Form.Label>
                  <Form.Control as="textarea" rows={3} name="excerpt" value={form.excerpt} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.CONTENT')}</Form.Label>
                  <TinymceEditor value={form.content} handleChange={value => setForm(current => ({ ...current, content: value }))} height="60vh" />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="border mb-4">
              <Card.Header>
                <h5 className="mb-0">{t('BLOG.SETTINGS_SECTION')}</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.CATEGORY')}</Form.Label>
                  <Form.Control name="category" value={form.category} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.AUTHOR_NAME')}</Form.Label>
                  <Form.Control name="author_name" value={form.author_name} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.READING_TIME')}</Form.Label>
                  <Form.Control name="reading_time" value={form.reading_time} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.COVER_IMAGE_URL')}</Form.Label>
                  <Form.Control name="cover_image_url" value={form.cover_image_url} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.ACCENT_COLOR')}</Form.Label>
                  <Form.Control type="color" name="accent_color" value={form.accent_color} onChange={handleChange} />
                </Form.Group>
                <Form.Check className="mb-3" type="checkbox" name="is_featured" label={t('BLOG.FIELDS.IS_FEATURED')} checked={form.is_featured} onChange={handleChange} />
                <Form.Check className="mb-0" type="checkbox" name="is_published" label={t('BLOG.FIELDS.IS_PUBLISHED')} checked={form.is_published} onChange={handleChange} />
              </Card.Body>
            </Card>

            <Card className="border mb-4">
              <Card.Header>
                <h5 className="mb-0">{t('BLOG.SEO_SECTION')}</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.META_TITLE')}</Form.Label>
                  <Form.Control name="meta_title" value={form.meta_title} onChange={handleChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t('BLOG.FIELDS.META_DESCRIPTION')}</Form.Label>
                  <Form.Control as="textarea" rows={4} name="meta_description" value={form.meta_description} onChange={handleChange} />
                </Form.Group>
              </Card.Body>
            </Card>

            <div className="d-flex gap-2">
              <Button type="submit" disabled={saving || !form.title.trim()}>
                {saving ? t('BLOG.SAVING') : t('BLOG.SAVE')}
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate('/blog')}>
                {t('BLOG.BACK_TO_LIST')}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default BlogEditorPage;
